import bronzeBadge from '@/assets/images/bronze-badge.svg';
import diamondBadge from '@/assets/images/diamond-badge.svg';
import goldBadge from '@/assets/images/gold-badge.svg';
import ironBadge from '@/assets/images/iron-badge.svg';
import legendaryBadge from '@/assets/images/legendary-badge.svg';
import platinumBadge from '@/assets/images/platinum-badge.svg';
import silverBadge from '@/assets/images/silver-badge.svg';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function randomColorGenerator() {
  // Generate random RGB values
  const red = Math.floor(Math.random() * 256);
  const green = Math.floor(Math.random() * 256);
  const blue = Math.floor(Math.random() * 256);

  // Calculate color brightness (luma)
  const brightness = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  // Set the threshold for dark/light background
  const threshold = 0.5;

  // Determine text color based on background brightness
  const textColor = brightness > threshold ? '#000000' : '#ffffff';

  // Convert RGB to hexadecimal for background color
  const hexColor = `#${red.toString(16).padStart(2, '0')}${green
    .toString(16)
    .padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;

  return {
    backgroundColor: hexColor,
    textColor,
  };
}

export function convertMinutesToHourMinute(minutes: number) {
  // Calculate hours and minutes
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  // Construct the result string
  let result = '';
  if (hours > 0) {
    result += hours + 'h';
    // if (hours !== 1) {
    //    result += 's'; // pluralize if necessary
    // }
    result += ' ';
  }
  if (remainingMinutes > 0) {
    result += remainingMinutes + 'm';
    // if (remainingMinutes !== 1) {
    //    result += 's'; // pluralize if necessary
    // }
  }
  return result;
}

export function convertMinutesToTimeString(minutes: number) {
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = '00';

  return `${formattedMinutes}:${formattedSeconds}`;
}

// export function convertSecondsToMinOrHoursWithText(seconds: number) {
//   const minutes = Math.floor(seconds / 60);
//   if (minutes >= 60) {
//     const hours = Math.floor(minutes / 60);
//     return `${hours} hours of on demand video`;
//   } else {
//     return `${minutes} mins of on demand video`;
//   }
// }

export function convertSecondsToMinOrHours(seconds: number, type: 'short' | 'full' = 'full') {
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours} hrs`;
  } else {
    return `${minutes}${type === 'full' ? ' mins' : 'm'}`;
  }
}

//2766="00h:46m:06s"
export function convertSecondsToTimeString(seconds: number, type: number = 1): string {
  const formatTime = (value: number) => String(value).padStart(2, '0');

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const timeParts: string[] = [];

  // Add hours if greater than 0 or if type requires it
  if (hours > 0 || type === 1 || type === 2) {
    timeParts.push(formatTime(hours));
  }

  // Add minutes if greater than 0 or if type requires it
  if (minutes > 0 || hours > 0 || type === 1 || type === 2 || type === 3) {
    timeParts.push(formatTime(minutes));
  }

  // Always include seconds if type is 1, 3, or if it’s greater than 0
  if (secs > 0 || type === 1 || type === 3) {
    timeParts.push(formatTime(secs));
  }

  // If no parts added, return just the hours, minutes, and seconds in plain format
  if (timeParts.length === 0) {
    return `${hours}:${minutes}:${secs}`;
  }

  return timeParts.join(':');
}

// Function to count the total number of chapters,lectures, completed lectures and completion percentage
type Lecture = {
  id: number;
  chapterId: number;
  title: string;
  slug: string;
  order: number;
  duration: number;
  type: string | null;
  link: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  completedLecture: any[];
};

type Chapter = {
  id: number;
  courseId: number;
  title: string;
  slug: string;
  order: number;
  task: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  lectures: Lecture[];
};

type Course = {
  id: number;
  userId: number;
  title: string;
  slug: string;
  thumbnailImage: string;
  price: number;
  offerPrice: number;
  coin: number;
  point: number;
  description: string;
  category: string;
  duration: number;
  createdAt: string;
  updatedAt: string;
  chapters: Chapter[];
};

export function countStats(course: Course) {
  let totalChapters = course?.chapters?.length;
  let totalLectures = 0;
  let totalTasks = 0;
  let completedLectures = 0;
  let totalDuration = 0;

  course?.chapters?.forEach(chapter => {
    totalLectures += chapter?.lectures?.length;
    totalTasks += chapter?.lectures?.filter(item => item?.type == 'task')?.length;
    completedLectures += chapter?.lectures?.filter(
      lecture => lecture?.completedLecture?.length > 0
    )?.length;
    totalDuration += chapter?.lectures?.reduce((acc, lecture) => acc + lecture?.duration, 0);
  });

  const completionPercentage =
    totalLectures === 0 ? '0' : ((completedLectures / totalLectures) * 100).toFixed(0);

  return {
    totalChapters,
    totalLectures,
    completedLectures,
    totalTasks,
    completionPercentage,
    totalDuration,
  };
}

export const loadScript = (src: string) => {
  return new Promise(resolve => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

export function getFirstLetters(inputString: string) {
  const words = inputString.split(' ');
  const firstLetter = words[0].charAt(0);
  const secondLetter = words[1] ? words[1].charAt(0) : '';
  return firstLetter + secondLetter;
}

export const defaultError = (traceId?: string) => ({
  title: 'Something went wrong ' + traceId,
  description: 'Please try again after sometime, Contact debugmedia@gmail.com for support',
  status: 400,
});

export const getOfferPercentage = (price: number, offerPrice: number): string => {
  try {
    // Validate inputs
    if (price <= 0 || offerPrice < 0) {
      throw new Error('Price must be greater than 0 and offer price must be non-negative.');
    }

    if (offerPrice > price) {
      throw new Error('Offer price cannot be greater than the original price.');
    }

    // Ensure precision by using Number for calculations
    const percentage = ((Number(price) - Number(offerPrice)) / Number(price)) * 100;

    // Return formatted percentage
    return percentage.toFixed(0);
  } catch (error: any) {
    console.error(error.message);
    return 'Invalid input';
  }
};

export function formatDateToDDMMMYYYY(date: Date): string {
  if (date) {
    const months = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];

    const newDate = new Date(date);
    const day = String(newDate.getDate()).padStart(2, '0');
    const month = months[newDate.getMonth()];
    const year = newDate.getFullYear();

    // Output: 20-NOV-2024 like format
    return `${day}-${month}-${year}`;
  }
  return '';
}

export function getTimeLeft(createdDate: Date, durationMonths: number) {
  const now = new Date();
  const created = new Date(createdDate);

  // Parse months and days from duration
  const [months, fractionalDays] = durationMonths.toString().split('.').map(Number);
  const duration = {
    months: months || 0,
    days: Math.round(((fractionalDays || 0) * 30) / 100), // Approximate fractional months to days
  };

  // Calculate expiry date
  const expiryDate = new Date(created);
  expiryDate.setMonth(expiryDate.getMonth() + duration.months);
  expiryDate.setDate(expiryDate.getDate() + duration.days);

  const timeDifference = expiryDate.getTime() - now.getTime();

  if (timeDifference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      timeDifference: 0,
    };
  }

  // Calculate time left
  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const daysLeft = Math.floor(hours / 24);

  return {
    days: daysLeft,
    hours: hours % 24,
    minutes: minutes % 60,
    seconds: seconds % 60,
    timeDifference: timeDifference,
  };
}

export const handleR2ImageUpload = async (thumbnail: File) => {
  try {
    if (!thumbnail) {
      throw new Error('No file selected');
    }

    const response = await fetch('/api/r2/upload', {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Failed to get upload URL');
    }

    const { url, filename } = await response.json();

    // Upload the file to R2
    const uploadResponse = await fetch(url, {
      method: 'PUT',
      body: thumbnail,
      headers: {
        'Content-Type': thumbnail.type, // Use the actual file type
      },
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('Upload failed:', errorText);
      throw new Error(`Failed to upload file: ${errorText}`);
    }

    return {
      success: true,
      filename,
      // url: `https://${process.env.CLOUDFLARE_BUCKET_NAME}.${process.env.CLOUDFLARE_ACCOUNT_ID}.r2.cloudflarestorage.com/${filename}`,
    };
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

export const ALL_BADGE = [
  {
    id: '1',
    name: 'Iron',
    icon: ironBadge,
    level: 1,
  },
  {
    id: '2',
    name: 'Bronze',
    icon: bronzeBadge,
    level: 2,
  },
  {
    id: '3',
    name: 'Silver',
    icon: silverBadge,
    level: 3,
  },
  {
    id: '4',
    name: 'Gold',
    icon: goldBadge,
    level: 4,
  },
  {
    id: '5',
    name: 'Platinum',
    icon: platinumBadge,
    level: 5,
  },
  {
    id: '6',
    name: 'Diamond',
    icon: diamondBadge,
    level: 6,
  },
  {
    id: '7',
    name: 'Legendary',
    icon: legendaryBadge,
    level: 7,
  },
];
